import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown, MessageSquareText } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

// FAQ content sourced from projects/roofclaw-seo-2026-05/faq/faq-content.md
// Voice: Adam Sand — direct, story-first, anti-victimhood, no filler.
// Six clusters — each renders a section header in the page.
const faqs = [
    // 1) Security & Ownership
    {
        cluster: "Security & Ownership",
        question: "Where does my RoofClaw data actually live?",
        answer: "On your machine, in your office. That's the whole point. Most 'AI for business' tools run in someone else's cloud — they keep your conversations, your CRM data, your customer info, and they decide who can see it. RoofClaw flips that. Your agent runs on the Mac we ship you. The machine sits on your desk or your bench. Your data stays there. We don't have a copy. There's nothing for a hacker to break into on our side because there's nothing of yours on our side."
    },
    {
        cluster: "Security & Ownership",
        question: "Can RoofClaw be hacked remotely?",
        answer: "Not in any normal sense. It's invisible to the public internet. We don't open any ports on your machine. There's no website to log into, no public IP, no attack surface. The only way to reach your agent remotely is through Tailscale — a private mesh network you control, locked to your identity. If someone wants to attack your RoofClaw, they'd have to physically walk into your office and sit at the keyboard."
    },
    {
        cluster: "Security & Ownership",
        question: "What if my Mac is stolen?",
        answer: "FileVault disk encryption is on by default. Without your password, the drive is a brick. We harden every machine before it ships using the Eubanks security guide. Admin and standard accounts are separated, so even if someone got past the login screen they'd have a hard time doing damage. API keys are stored in Apple's Secure Enclave — the same chip that protects your Face ID and Touch ID — and not in plain text anywhere."
    },
    {
        cluster: "Security & Ownership",
        question: "Do you have access to my agent or my data?",
        answer: "No. Once it ships, we don't have access. By design. We can help you fix it if something breaks. We can ship updates. But we are not logging into your machine and reading your customer messages. We've structured it this way because that's the only way roofers should trust this."
    },
    {
        cluster: "Security & Ownership",
        question: "What about my customers' data?",
        answer: "Same answer — it stays on your machine. We don't see it. If your agent reads a customer email, pulls a record from your CRM, or drafts a follow-up, all of that happens locally. Your CRM is still your CRM. RoofClaw is the worker that uses it. You own the worker, you own the data, you own the relationship."
    },

    // 2) What RoofClaw Actually Is
    {
        cluster: "What RoofClaw Actually Is",
        question: "What's the difference between RoofClaw and ChatGPT?",
        answer: "ChatGPT is a chatbot. RoofClaw is an employee. ChatGPT forgets you the moment you close the tab. RoofClaw remembers everything. ChatGPT doesn't know what's in your CRM. RoofClaw is connected to it. ChatGPT can't take action on your behalf. RoofClaw can update HubSpot, send a customer follow-up, log an inspection, and ping you on Telegram when something needs your eyes. Chatbots talk. Agents work."
    },
    {
        cluster: "What RoofClaw Actually Is",
        question: "What's the difference between RoofClaw and a SaaS chatbot from my CRM?",
        answer: "A SaaS chatbot is a feature. RoofClaw is a coworker. Those bolt-on bots are limited to what the CRM vendor decided to let them do this quarter. RoofClaw isn't limited to anything. If your CRM has an API, RoofClaw can use it. If a new tool comes out next year, your agent can learn it. You're not waiting for a vendor's roadmap. You're the one writing it."
    },
    {
        cluster: "What RoofClaw Actually Is",
        question: "Is RoofClaw just OpenClaw with extra steps?",
        answer: "OpenClaw is the engine. RoofClaw is the truck built around it. OpenClaw is the open-source agent runtime we use under the hood. It's powerful, but you have to know what you're doing to set it up. RoofClaw is OpenClaw, pre-configured for a roofing business, on hardware we hardened, with the integrations you actually use, with an agent identity built around how you actually work. You could do it yourself if you have a few hundred hours and the patience to debug. Or we can hand you the finished version next week."
    },
    {
        cluster: "What RoofClaw Actually Is",
        question: "Why a Mac and not a Windows machine?",
        answer: "Apple Silicon runs AI workloads faster, cooler, and longer than anything else at this price point. The M-series chips have dedicated neural hardware. We get more performance per watt, more battery life, and more local AI capability for the dollar than any equivalent Windows machine. We also trust the security model on macOS more — Secure Enclave for credentials, FileVault baked in, and a mature permissions system. Roofers don't need a gaming rig. They need a quiet, reliable second brain that boots up every morning and does its job."
    },
    {
        cluster: "What RoofClaw Actually Is",
        question: "Mac Mini or MacBook Air — which should I get?",
        answer: "Almost every client picks the MacBook Air. Get the Air. The Mini is fine if you have a permanent desk setup with a monitor. But the Air is portable, you can show people what your agent is doing without printing screenshots, and it works whether you're in the office, at the job site, or on a plane. There's no real performance difference for what RoofClaw does."
    },

    // 3) Connecting to Your Tools
    {
        cluster: "Connecting to Your Tools",
        question: "Will RoofClaw work with my CRM?",
        answer: "If your CRM has an open API, yes. The big ones do. We've connected RoofClaw to ProLine, HubSpot, AccuLynx, JobNimbus, RoofLink, Zuper, and CompanyCam. We've worked with QuickBooks for accounting integrations. We've connected Google Ads and Facebook Ads for campaign reporting. If you're on something we haven't seen, send us the API docs in your pre-consult and we'll tell you straight whether it's worth integrating."
    },
    {
        cluster: "Connecting to Your Tools",
        question: "How many integrations come with the build?",
        answer: "Three at build. Add more anytime. Your $10,000 build includes three API integrations connected, tested, and working. After delivery, you (or we) can add more. The agent is designed to learn new tools — it's not locked into the three we set up."
    },
    {
        cluster: "Connecting to Your Tools",
        question: "What if I switch CRMs later?",
        answer: "The process doesn't change. Only the connection does. Your process — get a lead, inspect, quote, contract, invoice — doesn't change when you switch tools. The agent has the process memorized. If you move from one CRM to another, we just point the agent at the new API. Your agent doesn't have to be retrained on the work. It just has to be told where the new filing cabinet is."
    },
    {
        cluster: "Connecting to Your Tools",
        question: "Can my whole team use RoofClaw?",
        answer: "Yes, but start with one user. Give it to yourself or your most adaptable employee first. Make it generate so much value that you'd want to buy a second one. Once you've crossed that line, we can talk about a second RoofClaw for a specific employee — and that's actually the cleanest way to scale, because each person gets their own coworker with their own context."
    },

    // 4) Buying & Onboarding
    {
        cluster: "Buying & Onboarding",
        question: "What does the build actually look like?",
        answer: "You buy. We talk. We build. We ship. You train. You go. (1) You purchase — the clock starts. (2) We schedule a 1-hour pre-consult with Adam. We design your agent's identity — what it's called, what it does, what it's protecting against. (3) We build your dossier: every detail we need to make the agent fit your business. (4) We configure a Mac, harden it, connect your three integrations, and load your agent. (5) We ship the machine to your office. (6) We get on a 3-4 hour call together. Your agent goes live. We train you and your team. (7) You join Winning Wednesday and the RoofClaw Community. You're never on your own."
    },
    {
        cluster: "Buying & Onboarding",
        question: "How long does it take from purchase to go-live?",
        answer: "Usually 2-4 weeks, depending on how fast you get us credentials. The bottleneck is almost never us. It's waiting for API keys, CRM access, or sometimes just getting our shared calendar to line up. Roofers who move fast on credentials get live faster. Roofers who let it sit do not."
    },
    {
        cluster: "Buying & Onboarding",
        question: "What do I need to do before the build?",
        answer: "Three things, and we walk you through every one. (1) We help you set up an Apple ID for your agent (we do this part). (2) You set up a free Tailscale account (5 minutes at tailscale.com). (3) You hand us the API keys for the three tools you want connected — securely, via Password Pusher. We never want credentials sitting in an email."
    },
    {
        cluster: "Buying & Onboarding",
        question: "What if I get stuck after delivery?",
        answer: "You don't get stuck alone. Every RoofClaw client has Tony 'The Wrench' Clawzini — an AI mechanic agent that helps you fix your own RoofClaw when it acts up. Plus the RoofClaw Community Telegram group, monitored daily. Plus Winning Wednesday — a weekly group call where we work through real problems together. Plus direct messaging with Vito for support questions. We've designed the support layer for roofers who run busy businesses."
    },

    // 5) Money
    {
        cluster: "Money",
        question: "Why $10,000? Why not a subscription?",
        answer: "Because you should own this, not rent it. A subscription means you're permanently paying someone else for permission to use your own business tool. We don't think that's how AI for serious businesses should work. You pay once, you own the hardware, you own the agent, and your only ongoing cost is the cloud AI tokens you choose to consume (usually $50-$300/month depending on usage). Compare that to $1,000+/month for the kind of SaaS stack roofers normally pile on, and the math gets very simple very fast."
    },
    {
        cluster: "Money",
        question: "What's the ROI math?",
        answer: "One saved deal usually pays for it. If RoofClaw helps you close one extra $20,000 roof in the first year — by following up faster, answering after-hours, or freeing you up to do sales instead of admin — it has more than paid for itself. Most clients see that in the first few weeks. If you've ever lost a deal because you didn't get back to the customer fast enough, you already know the cost of not having this."
    },
    {
        cluster: "Money",
        question: "Is there a payment plan?",
        answer: "Not currently. Cash up front. If that's a hard no for you, that's fine — book the $800 AI Readiness Session first. It credits toward the full build if you move forward, and it gives you a real plan before you commit."
    },
    {
        cluster: "Money",
        question: "What's the refund policy?",
        answer: "100% refund before the machine ships. No refunds after. Once the hardware is on a truck and headed to your door, there's no take-backs. Before that, you can change your mind for any reason and we'll send your money back, no questions asked. We don't want to ship a RoofClaw to someone who isn't sure yet."
    },
    {
        cluster: "Money",
        question: "What about ongoing costs after the 6-month community trial?",
        answer: "$99/month for the community, plus your own AI usage. After six months free, RoofClaw Community is $99/month. That keeps you in the group, gets you Winning Wednesday access, and keeps Vito monitoring the channel. AI token usage (OpenAI, Anthropic, etc.) is separate — that's whatever you spend with the model provider directly, billed by them, capped however you want."
    },

    // 6) Trust
    {
        cluster: "Trust",
        question: "Who actually builds my RoofClaw?",
        answer: "Adam Sand. Personally. Adam founded Roofing Business Partner in 2017. He coaches over 40 roofing companies. He built the first RoofClaw on his own bench, with his own hands, and he still does the pre-consult call on every build personally. The team helps with logistics and configuration. The agent identity, the integration design, the strategic fit — that's Adam."
    },
    {
        cluster: "Trust",
        question: "Can I talk to someone who actually owns one?",
        answer: "Yes. Names on request after your pre-consult. We don't put a wall of logos and stock photos on the site. If you want to talk to a real roofer using RoofClaw, we'll connect you after your consult so you can ask them anything. The product sells itself when you talk to someone who's living with it."
    },
    {
        cluster: "Trust",
        question: "What happens if RoofClaw the company shuts down?",
        answer: "Your agent keeps running. It's on your machine. The hardware is yours. The agent is yours. OpenClaw is open source. The integrations are configured with your credentials, in your control. RoofClaw the company existing or not existing has no bearing on whether your agent keeps working. We've built it that way on purpose, because you should never have a critical business tool that depends on another company's survival."
    },
    {
        cluster: "Trust",
        question: "Why should I trust you with my business?",
        answer: "Because Adam built his businesses the same way he's helping you build yours. He's a Christian. He runs his businesses the way he wants to run his life: hungry, humble, smart. He doesn't promise what he can't deliver, and he doesn't sell to people who shouldn't buy. Talk to him on a pre-consult. If he thinks you're not ready, he'll tell you. If he thinks you are, he'll tell you exactly why, and we'll get to work."
    }
];

const FAQPage = () => {
    const listRef = useRef(null);
    const [openIndex, setOpenIndex] = useState(0);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo('.faq-item',
                {
                    y: 30,
                    opacity: 0,
                },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.05,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: listRef.current,
                        start: 'top 85%'
                    }
                }
            );
        }, listRef);
        return () => ctx.revert();
    }, []);

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": "https://www.roofclaw.com/faq#faqpage",
        "url": "https://www.roofclaw.com/faq",
        "isPartOf": { "@id": "https://www.roofclaw.com/#organization" },
        "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };

    return (
        <div className="pt-32 pb-32 bg-[#0A0A14] text-[#F0EFF4] min-h-screen selection:bg-[#7B61FF] selection:text-[#F0EFF4]">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
            <div className="max-w-4xl mx-auto px-6">

                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-xs font-semibold tracking-wider text-[#7B61FF] mb-6">
                        <MessageSquareText className="w-4 h-4" />
                        SYSTEM KNOWLEDGE BASE
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">Frequently Asked<br /><span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-5xl md:text-7xl">Questions.</span></h1>
                    <p className="text-lg text-[#F0EFF4]/60 max-w-2xl mx-auto">
                        Straight answers on security, the build, the money, and the trust. No filler.
                    </p>
                </div>

                <div className="space-y-4" ref={listRef}>
                    {faqs.map((faq, index) => {
                        const isOpen = openIndex === index;
                        const prevCluster = index > 0 ? faqs[index - 1].cluster : null;
                        const showClusterHeader = faq.cluster && faq.cluster !== prevCluster;
                        return (
                            <React.Fragment key={index}>
                                {showClusterHeader && (
                                    <h2 className="faq-item pt-8 pb-2 text-xs font-data tracking-widest uppercase text-[#7B61FF]">
                                        {faq.cluster}
                                    </h2>
                                )}
                                <div
                                    className={`faq-item group border transition-all duration-500 rounded-3xl overflow-hidden ${isOpen ? 'bg-[#18181B] border-[#7B61FF]/40 shadow-[0_0_20px_rgba(123,97,255,0.1)]' : 'bg-[#0A0A14] border-[#7B61FF]/10 hover:border-[#7B61FF]/30 hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(123,97,255,0.05)]'}`}
                                >
                                    <button
                                        onClick={() => setOpenIndex(isOpen ? -1 : index)}
                                        className="w-full text-left px-8 py-6 flex items-center justify-between gap-4 group-hover:px-10 transition-all duration-300"
                                    >
                                        <span className="font-bold text-lg pr-8">{faq.question}</span>
                                        <ChevronDown className={`w-5 h-5 text-[#7B61FF] shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    <div
                                        className={`px-8 overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[80rem] pb-6 opacity-100' : 'max-h-0 opacity-0'}`}
                                    >
                                        <p className="text-[#F0EFF4]/70 leading-relaxed border-t border-[#7B61FF]/20 pt-4">
                                            {faq.answer}
                                        </p>
                                    </div>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>

            </div>
        </div>
    );
};

export default FAQPage;
